/**
 * Two-mode scheduling primitives.
 *
 *   jobMode ∈ { 'route_flexible', 'appointment' }
 *   hasTime: boolean — whether the hour/minute on scheduledStart is real
 *
 * jobMode is *intent* (customer commitment vs flexible). hasTime is *data*
 * (does the renderer have a real time to show). They correlate today but
 * are kept separate so future modes (time_window, etc.) don't force every
 * renderer to learn the mode enum.
 */
export const MODE_ROUTE = 'route_flexible';
export const MODE_APPT  = 'appointment';

export function isAppointment(task) {
  return (task?.jobMode || '') === MODE_APPT;
}

export function isRouteFlexible(task) {
  // Default-when-missing: route. Matches Build Day's existing inference.
  const m = task?.jobMode || MODE_ROUTE;
  return m === MODE_ROUTE;
}

/**
 * Normalize a task write payload against the invariant:
 *   - Appointment: requires real time-of-day on scheduledStart; hasTime=true.
 *   - Route flexible: hour/minute stripped to T00:00:00Z; hasTime=false.
 *
 * Pure function. Returns the cleaned payload. Throws only on explicit
 * appointment-without-time — every other ambiguity resolves silently to
 * route default.
 */
export function normalizeTaskWrite(payload) {
  if (!payload || typeof payload !== 'object') return payload;
  const out = { ...payload };
  const mode = out.jobMode || (out.scheduledStart && !out.serviceDate ? MODE_APPT : MODE_ROUTE);
  out.jobMode = mode;

  if (mode === MODE_APPT) {
    if (!out.scheduledStart) {
      throw new Error('Appointment-mode jobs require scheduledStart with time');
    }
    out.hasTime = true;
    return out;
  }

  // Route flexible — strip time from scheduledStart, set hasTime=false.
  // The user picked a LOCAL calendar day; we must preserve it across reads
  // AND across the backend recurrence engine, which calls fromisoformat()
  // on this string and adds interval_days.
  //
  // ⚠ Iter-254 fix — DO NOT use getUTC{FullYear,Month,Date} + 'T00:00:00Z'.
  // UTC midnight resolves to "the day before" in every negative-offset
  // timezone (all of the US): pick Aug 13 in CDT → stored as
  // '2026-08-13T00:00:00.000Z' → readers in CDT see Aug 12 19:00 local →
  // .getDate() returns 12. Stored Aug 13, rendered Aug 12.
  //
  // Use LOCAL components (the day the user actually picked) and anchor at
  // UTC NOON. UTC noon is stable in any timezone from UTC-11 through
  // UTC+11 (covers US, Europe, most of Asia). 12:00 UTC is 01:00 the next
  // day in UTC+13 (Samoa) — acceptable edge given CleanBid's customer base
  // and far safer than the midnight anchor.
  if (out.scheduledStart) {
    const d = new Date(out.scheduledStart);
    if (!Number.isNaN(d.getTime())) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      out.scheduledStart = `${y}-${m}-${day}T12:00:00.000Z`;
      out.serviceDate = `${y}-${m}-${day}`;
    }
  }
  out.hasTime = false;
  return out;
}
